import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import logger from '../../utils/logger';
import { api } from '../../services/api';
import { loadArticleHtml, prepareArticleQuill } from '../../utils/articlePaste';
import './GeneralVocabularyManagement.css';

const API_BASE_URL = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');

const GeneralVocabularyManagement = ({ onBack, initialSection = 'overview', selectedLanguage = 'English' }) => {
  const { t } = useTranslation('generalVocabularyManagement');
  
  // UI state mirrors ExamManagement
  const [currentSection, setCurrentSection] = useState(initialSection);
  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editArticleId, setEditArticleId] = useState(null);

  // Data state
  const [articlesByType, setArticlesByType] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    cefr_level: '',
    content_type: '',
    genre: '',
    estimated_read_time: '',
    word_count: '',
    content: '',
  });

  // Quill
  const quillRef = useRef(null);
  const [useQuill, setUseQuill] = useState(false);

  // Jobs (placeholder list until backend processing is wired for general vocabulary)
  const [jobsLoading, setJobsLoading] = useState(false);
  const [jobsError, setJobsError] = useState('');
  const [jobsStatusFilter, setJobsStatusFilter] = useState('');
  const [jobs, setJobs] = useState([]);
  const [jobsArticleFilter, setJobsArticleFilter] = useState('');
  const [jobByArticleId, setJobByArticleId] = useState({});

  useEffect(() => {
    setCurrentSection(initialSection);
    // eslint-disable-next-line
  }, [initialSection]);

  useEffect(() => {
    if (currentSection === 'jobs') {
      fetchJobs();
    }
    // eslint-disable-next-line
  }, [currentSection, jobsStatusFilter, jobsArticleFilter]);

  useEffect(() => {
    // Best-effort: keep job badges in sync for the currently selected content type section
    if (currentSection && currentSection !== 'overview' && currentSection !== 'jobs') {
      fetchJobsForBadges(currentSection);
    } else {
      setJobByArticleId({});
    }
    // eslint-disable-next-line
  }, [currentSection]);

  async function fetchJobs() {
    setJobsLoading(true);
    setJobsError('');
    try {
      const params = new URLSearchParams();
      if (jobsStatusFilter) params.set('status', jobsStatusFilter);
      if (jobsArticleFilter) params.set('article_id', jobsArticleFilter);
      const result = await api.get(`/api/admin/general-vocabulary-processing-jobs?${params.toString()}`);
      setJobs(result.jobs || []);
    } catch (e) {
      setJobsError(e.message || 'Failed to load jobs');
      setJobs([]);
    } finally {
      setJobsLoading(false);
    }
  }

  async function fetchJobsForBadges(contentTypeId) {
    try {
      const params = new URLSearchParams();
      params.set('limit', '1000');
      if (contentTypeId) params.set('content_type', contentTypeId);
      const result = await api.get(`/api/admin/general-vocabulary-processing-jobs?${params.toString()}`);
      const rows = result.jobs || [];
      const map = {};
      rows.forEach((j) => {
        if (j?.article_id != null) map[j.article_id] = j;
      });
      setJobByArticleId(map);
    } catch (e) {
      setJobByArticleId({});
    }
  }

  async function reprocessArticle(articleId) {
    try {
      await api.post(`/api/admin/general-vocabulary/articles/${Number(articleId)}/reprocess-words`, {});
      alert('Reprocess queued successfully');
      fetchJobs();
      fetchJobsForBadges(currentSection !== 'overview' && currentSection !== 'jobs' ? currentSection : null);
    } catch (e) {
      alert(e.message || 'Failed to queue reprocess');
    }
  }

  function openJobsForArticle(articleId) {
    setCurrentSection('jobs');
    setJobsArticleFilter(String(articleId));
  }

  // ✅ ENGLISH KEYS (for API & database) - NEVER TRANSLATE THESE
  // Content types with English keys for API, translated labels for UI
  const contentTypes = [
    { id: 'story', name: t('contentTypes.story'), icon: 'menu_book' },
    { id: 'news', name: t('contentTypes.news'), icon: 'newspaper' },
    { id: 'conversation', name: t('contentTypes.conversation'), icon: 'chat' },
    { id: 'dialogue', name: t('contentTypes.dialogue'), icon: 'record_voice_over' },
    { id: 'poem', name: t('contentTypes.poem'), icon: 'draw' },
    { id: 'essay', name: t('contentTypes.essay'), icon: 'edit' },
    { id: 'novel', name: t('contentTypes.novel'), icon: 'auto_stories' },
    { id: 'vocabulary', name: t('contentTypes.vocabulary'), icon: 'library_books' }
  ];

  const cefrLevels = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];

  // Init Quill when form opens
  useEffect(() => {
    if (showForm && window.Quill && quillRef.current && !useQuill) {
      setUseQuill(true);
      quillRef.current.innerHTML = '';
      const quill = new window.Quill(quillRef.current, {
        theme: 'snow',
        modules: {
          toolbar: [
            ['bold', 'italic', 'underline'],
            ['link', 'blockquote', 'code-block'],
            [{ list: 'ordered' }, { list: 'bullet' }],
            [{ header: [1, 2, 3, false] }],
            ['clean']
          ]
        },
        placeholder: t('form.contentPlaceholder')
      });
      quill.on('text-change', () => {
        setFormData(f => ({ ...f, content: quill.root.innerHTML }));
      });
      prepareArticleQuill(quill);
    }
  }, [showForm, t, useQuill]);

  // Fetch materials for selected content type
  async function fetchArticlesByContentType(typeId) {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      params.set('content_type', typeId);
      if (selectedLanguage) params.set('language', selectedLanguage);
      const res = await fetch(`${API_BASE_URL}/api/general-vocabulary/articles?${params.toString()}`);
      if (!res.ok) throw new Error('Failed to fetch materials');
      const data = await res.json();
      setArticlesByType(prev => ({ ...prev, [typeId]: data }));
    } catch (e) {
      logger.error('Error fetching articles by content type:', e);
      setError(t('messages.errorSaving'));
      setArticlesByType(prev => ({ ...prev, [typeId]: [] }));
    } finally {
      setLoading(false);
    }
  }

  // Open add form
  function openForm() {
    setShowForm(true);
    setIsEditing(false);
    setEditArticleId(null);
    setFormData({ title: '', cefr_level: '', content_type: currentSection, genre: '', estimated_read_time: '', word_count: '', content: '' });
    setTimeout(() => {
      if (window.Quill && quillRef.current) {
        const quill = window.Quill.find(quillRef.current);
        if (quill) quill.root.innerHTML = '';
      }
    }, 200);
  }

  // Edit existing
  function handleEdit(article) {
    setShowForm(true);
    setIsEditing(true);
    setEditArticleId(article.id);
    setFormData({
      title: article.title || '',
      cefr_level: article.cefr_level || '',
      content_type: article.content_type || currentSection,
      genre: article.genre || '',
      estimated_read_time: article.estimated_read_time || '',
      word_count: article.word_count || '',
      content: article.content || ''
    });
    setTimeout(() => {
      if (window.Quill && quillRef.current) {
        const quill = window.Quill.find(quillRef.current);
        if (quill) loadArticleHtml(quill, article.content || '');
      }
    }, 200);
  }

  // Submit form
  async function handleFormSubmit(e) {
    e.preventDefault();

    if (!formData.title || !formData.content_type || !formData.cefr_level || !formData.content) {
      alert(t('messages.fillRequiredFields'));
      return;
    }

    const payload = {
      title: formData.title,
      content: formData.content,
      cefr_level: formData.cefr_level,
      content_type: formData.content_type,
      genre: formData.genre || null,
      estimated_read_time: formData.estimated_read_time ? Number(formData.estimated_read_time) : null,
      word_count: formData.word_count ? Number(formData.word_count) : null,
      language: selectedLanguage || 'English',
      level: 'beginner',
      specialty: 'general',
      teacher_id: 1,
      tags: []
    };

    try {
      let res;
      if (isEditing && editArticleId) {
        res = await fetch(`${API_BASE_URL}/api/general-vocabulary/articles/${editArticleId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      } else {
        res = await fetch(`${API_BASE_URL}/api/general-vocabulary/articles`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      }

      if (!res.ok) throw new Error('Failed to save');

      setShowForm(false);
      setIsEditing(false);
      setEditArticleId(null);
      await fetchArticlesByContentType(currentSection);
      alert(isEditing ? t('messages.materialUpdated') : t('messages.materialCreated'));
    } catch (err) {
      logger.error('Error saving material:', err);
      alert(t('messages.errorSaving'));
    }
  }

  // Delete material
  async function handleDelete(article) {
    if (!window.confirm(t('messages.deleteConfirm', { title: article.title }))) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/general-vocabulary/articles/${article.id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Failed to delete');
      await fetchArticlesByContentType(currentSection);
      alert(t('messages.materialDeleted'));
    } catch (err) {
      logger.error('Error deleting material:', err);
      alert(t('messages.errorDeleting'));
    }
  }

  const renderOverview = () => (
    <div className="gv-overview fade-in">
      <div className="gv-header">
        <h2>{t('overview.title')}</h2>
        <p>{t('overview.subtitle')}</p>
      </div>
      <div className="gv-cards">
        {contentTypes.map((type) => (
          <div key={type.id} className="gv-card card-animate" onClick={() => { setCurrentSection(type.id); fetchArticlesByContentType(type.id); }}>
            <div className="gv-card-header">
              <span className="material-icons">{type.icon}</span>
              <h3>{type.name}</h3>
            </div>
            <div className="gv-card-content">
              <p>{t(`contentTypesDescription.${type.id}`)}</p>
            </div>
            <div className="gv-card-footer">
              <span className="access-link">{t('actions.accessModule')}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderJobs = () => (
    <div className="gv-overview fade-in">
      <div className="gv-header">
        <h2>Translation Jobs</h2>
        <p>Monitor and reprocess General Vocabulary translation preprocessing jobs.</p>
      </div>

      <div className="jobs-header">
        <h3>Jobs</h3>
        <div className="jobs-controls">
          <select value={jobsStatusFilter} onChange={(e) => setJobsStatusFilter(e.target.value)}>
            <option value="">All statuses</option>
            <option value="not_started">not_started</option>
            <option value="pending">pending</option>
            <option value="running">running</option>
            <option value="succeeded">succeeded</option>
            <option value="failed">failed</option>
          </select>
            {jobsArticleFilter ? (
              <button type="button" className="btn-secondary" onClick={() => setJobsArticleFilter('')}>
                Show all
              </button>
            ) : null}
          <button type="button" className="btn-secondary" onClick={fetchJobs}>Refresh</button>
        </div>
      </div>

      {jobsLoading ? (
        <div className="loading">Loading jobs...</div>
      ) : jobsError ? (
        <div className="empty-state">
          <span className="material-icons empty-icon">error_outline</span>
          <p>{jobsError}</p>
        </div>
      ) : jobs.length === 0 ? (
        <div className="empty-state">
          <span className="material-icons empty-icon">inbox</span>
          <p>No jobs found.</p>
        </div>
      ) : (
        <div className="jobs-table-wrapper">
          <table className="jobs-table">
            <thead>
              <tr>
                <th>Article</th>
                <th>Status</th>
                <th>Attempts</th>
                <th>Last error</th>
                <th>Started</th>
                <th>Finished</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((j) => (
                <tr key={j.article_id} className={jobsArticleFilter && String(j.article_id) === String(jobsArticleFilter) ? 'job-row-highlight' : ''}>
                  <td>
                    <div className="job-article-cell">
                      <div className="job-article-title">{j.article_title || `#${j.article_id}`}</div>
                      <div className="job-article-meta">
                        #{j.article_id}{j.content_type ? ` · ${j.content_type}` : ''}{j.cefr_level ? ` · ${j.cefr_level}` : ''}
                      </div>
                    </div>
                  </td>
                  <td><span className={`job-status ${j.status}`}>{j.status}</span></td>
                  <td>{j.attempts}</td>
                  <td className="job-error">{j.last_error || ''}</td>
                  <td>{j.started_at ? new Date(j.started_at).toLocaleString() : ''}</td>
                  <td>{j.finished_at ? new Date(j.finished_at).toLocaleString() : ''}</td>
                  <td>
                    <button
                      type="button"
                      className="btn-primary"
                      onClick={() => reprocessArticle(j.article_id)}
                    >
                      Reprocess
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderContentSection = () => {
    const current = contentTypes.find(t => t.id === currentSection);
    const items = articlesByType[currentSection] || [];

    return (
      <div className="gv-section fade-in">
        <div className="gv-section-header">
          <div className="header-top">
            <button className="back-btn" onClick={() => setCurrentSection('overview')} title={t('actions.back')}>
              <span className="material-icons">arrow_back</span>
              {t('actions.back')}
            </button>
            <h2>{current?.name}</h2>
          </div>
          <p>{t('messages.manageFor', { type: current?.name })}</p>
        </div>

        <div className="gv-controls">
          <div className="control-row">
            <div className="search-box">
              <input type="text" placeholder={t('filters.searchPlaceholder')} onChange={(e) => {
                const q = e.target.value.toLowerCase();
                const all = articlesByType[currentSection] || [];
                const filtered = all.filter(a => `${a.title}`.toLowerCase().includes(q));
                setArticlesByType(prev => ({ ...prev, [currentSection]: filtered }));
              }} />
              <span className="material-icons">search</span>
            </div>
            <div className="selectors">
              <select value={formData.cefr_level} onChange={e => setFormData(f => ({ ...f, cefr_level: e.target.value }))}>
                <option value="">{t('filters.filterByCefr')}</option>
                {cefrLevels.map(l => (<option key={l} value={l}>{l}</option>))}
              </select>
            </div>
          </div>
          <button className="add-material-btn" onClick={openForm}>
            <span className="material-icons">add</span>
            {t('actions.addNewMaterial')}
          </button>
        </div>

        <div className="gv-articles">
          <h3>{current?.name} {t('messages.materials')}</h3>
          {loading ? (
            <div className="loading">{t('messages.loadingMaterials')}</div>
          ) : error ? (
            <div className="empty-state">
              <span className="material-icons empty-icon">error_outline</span>
              <p>{error}</p>
            </div>
          ) : items.length === 0 ? (
            <div className="empty-state">
              <span className="material-icons empty-icon">article</span>
              <p>{t('messages.noMaterialsFound')}</p>
            </div>
          ) : (
            <div className="articles-grid">
              {items.map(article => (
                <div key={article.id} className="article-card card-animate">
                  <div className="article-card-header">
                    <span className="content-type-badge">{article.cefr_level || '—'}</span>
                    <div className="article-header-right">
                      <span className="article-date">{article.created_at ? new Date(article.created_at).toLocaleDateString() : ''}</span>
                      <button
                        type="button"
                        className={`job-status ${jobByArticleId?.[article.id]?.status || 'not_started'} article-job-badge`}
                        title="Open Translation Jobs for this article"
                        onClick={() => openJobsForArticle(article.id)}
                      >
                        {jobByArticleId?.[article.id]?.status || 'not_started'}
                      </button>
                      {(jobByArticleId?.[article.id]?.status === 'failed') && (
                        <button
                          type="button"
                          className="job-reprocess-mini"
                          title="Reprocess word translations"
                          onClick={() => reprocessArticle(article.id)}
                        >
                          Reprocess
                        </button>
                      )}
                    </div>
                  </div>
                  <h4 className="article-title">{article.title}</h4>
                  <div className="article-meta">
                    <span className="meta-pill">{article.genre || current?.name}</span>
                    <span className="meta-pill">{article.estimated_read_time ? `${article.estimated_read_time} ${t('meta.min')}` : '—'}</span>
                    <span className="meta-pill">{article.word_count ? `${article.word_count} ${t('meta.words')}` : '—'}</span>
                  </div>
                  <div className="article-preview">
                    <div className="preview-text">
                      {article.content ? (() => {
                        const clean = article.content.replace(/<[^>]*>/g, '').trim();
                        const preview = clean.substring(0, 140);
                        return preview.length < clean.length ? `${preview}...` : preview || '—';
                      })() : '—'}
                    </div>
                  </div>
                  <div className="article-actions">
                    <button className="action-btn edit" title={t('actions.edit')} onClick={() => handleEdit(article)}>
                      <span className="material-icons">edit</span> {t('actions.edit')}
                    </button>
                    <button className="action-btn delete" title={t('actions.delete')} onClick={() => handleDelete(article)}>
                      <span className="material-icons">delete</span> {t('actions.delete')}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderForm = () => (
    <div className={`gv-overlay ${showForm ? 'show' : ''}`}>
      <div className="gv-form">
        <div className="form-header">
          <h3>{isEditing ? t('form.editMaterial') : t('form.addNewMaterial')}</h3>
          <button className="close-btn" onClick={() => setShowForm(false)}>
            <span className="material-icons">close</span>
          </button>
        </div>
        <form onSubmit={handleFormSubmit} className="form-content">
          <div className="form-group">
            <label htmlFor="gv-title">{t('form.title')}:</label>
            <input id="gv-title" type="text" placeholder={t('form.titlePlaceholder')} value={formData.title} onChange={e => setFormData(f => ({ ...f, title: e.target.value }))} required />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="gv-type">{t('form.type')}:</label>
              <select id="gv-type" value={formData.content_type} onChange={e => setFormData(f => ({ ...f, content_type: e.target.value }))} required>
                <option value="">{t('form.selectContentType')}</option>
                {contentTypes.map(type => (<option key={type.id} value={type.id}>{type.name}</option>))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="gv-level">{t('form.cefrLevel')}:</label>
              <select id="gv-level" value={formData.cefr_level} onChange={e => setFormData(f => ({ ...f, cefr_level: e.target.value }))} required>
                <option value="">{t('form.selectCefrLevel')}</option>
                {cefrLevels.map(l => (<option key={l} value={l}>{l}</option>))}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="gv-genre">{t('form.genre')}:</label>
              <input id="gv-genre" type="text" placeholder={t('form.genrePlaceholder')} value={formData.genre} onChange={e => setFormData(f => ({ ...f, genre: e.target.value }))} />
            </div>
            <div className="form-group">
              <label htmlFor="gv-readtime">{t('form.readingTime')}:</label>
              <input id="gv-readtime" type="number" min="0" value={formData.estimated_read_time} onChange={e => setFormData(f => ({ ...f, estimated_read_time: e.target.value }))} />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="gv-wordcount">{t('form.wordCount')}:</label>
              <input id="gv-wordcount" type="number" min="0" value={formData.word_count} onChange={e => setFormData(f => ({ ...f, word_count: e.target.value }))} />
            </div>
          </div>

          <div className="form-group">
            <label>{t('form.content')}:</label>
            <div style={{ display: useQuill ? 'block' : 'none' }}>
              <div id="gv-editor" ref={quillRef} style={{ minHeight: 200, background: '#fff', borderRadius: 8 }} />
            </div>
            <textarea
              id="gv-fallback-editor"
              style={{ display: useQuill ? 'none' : 'block', width: '100%', minHeight: 200, padding: 12, border: '2px solid #e1e5e9', borderRadius: 8, fontSize: 16 }}
              placeholder={t('form.contentPlaceholder')}
              value={formData.content}
              onChange={e => setFormData(f => ({ ...f, content: e.target.value }))}
            />
          </div>

          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={() => setShowForm(false)}>{t('actions.cancel')}</button>
            <button type="submit" className="btn-primary">{isEditing ? t('actions.update') : t('actions.submit')}</button>
          </div>
        </form>
      </div>
    </div>
  );

  return (
    <div className="gv-management-scroll-wrapper">
      <div className="gv-management-page">
        {/* Inner tabs (Content / Translation Jobs) */}
        <div className="vocabulary-inner-tabs">
          <button
            className={`vocabulary-inner-tab ${currentSection !== 'jobs' ? 'active' : ''}`}
            onClick={() => setCurrentSection('overview')}
          >
            <span className="material-icons">dashboard</span>
            Content
          </button>
          <button
            className={`vocabulary-inner-tab ${currentSection === 'jobs' ? 'active' : ''}`}
            onClick={() => setCurrentSection('jobs')}
          >
            <span className="material-icons">sync</span>
            Translation Jobs
          </button>
        </div>

        {currentSection === 'jobs' ? renderJobs() : (currentSection === 'overview' ? renderOverview() : renderContentSection())}
        {renderForm()}
      </div>
    </div>
  );
};

export default GeneralVocabularyManagement;
